import {
  nachflussLaeuft,
  nurVorraete,
  ZEITFENSTER,
  ZEITFENSTER_LABELS,
  zaehltAlsEntwickler,
  type Zeitfenster,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { poolsQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { usePersonenDieserWelt, usePersonenFuerAbnahme } from '../lib/welt'
import { PersonenOptionen } from './PersonenOptionen'
import { Button } from './ui/primitives'
import { VorhabenBeteiligte } from './VorhabenEinladen'
import type { VorhabenEintrag } from './VorhabenZeile'

/**
 * **Die Felder eines Vorhabens – Bausteine des einen Vorhaben-Fensters** (23.10.2026).
 *
 * Nils: *„irgendwie gibt es zu viele Fenster mit Einstellungen, Details usw., die teilweise alles
 * das selbe machen."* Bis dahin gab es ein Einstellungs-Panel neben der Liste (26.08.2026, Entwurf
 * `infos/vorhaben-standalone.html`) **und** das Vorhaben-Detail – mit Mit-Owner, Eingeladenen und
 * vor allem der Beschreibung an beiden Stellen: einmal als Textfeld, einmal als Markdown-Editor,
 * dasselbe Feld. Entschieden: **ein Fenster**, das Detail. Das Panel ist in diese drei Bausteine
 * zerlegt und dort eingesetzt.
 *
 * Gespeichert wird weiter **beim Verlassen des Feldes**, jede Änderung einzeln (Leitplanke 1) –
 * deshalb gibt es kein „Speichern".
 */

/** Verwirft alles, worauf eine Einstellung hier wirkt – Vorhaben, Aufgaben, Pools. */
function useNeuLaden(): () => Promise<void> {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
    await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    await queryClient.invalidateQueries({ queryKey: ['pools'] })
  }
}

type Aenderung = {
  label?: string
  typ?: 'projekt' | 'thema'
  entwicklungsart?: 'klassisch' | 'ki' | 'gemischt'
  zieltermin?: Date | null
  zeitfenster?: Zeitfenster | null
  poolId?: string | null
  nachflussLimit?: number | null
  nachflussGewuenscht?: boolean
}

/**
 * **Jede Änderung folgt dem Klick sofort** (Leitplanke 1). Statt einer Überlagerung je Feld wird
 * der Zwischenspeicher selbst geflickt – dann zeigen Liste, Kopf und Feld denselben Stand. Bei
 * einem Fehler zurück auf den alten Stand, mit Hinweis. Bis 23.10. galt das nur für den Typ.
 */
function useAendern(vorhabenId: string) {
  const queryClient = useQueryClient()
  const neuLaden = useNeuLaden()
  const [fehler, setFehler] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: (felder: Aenderung) => trpc.vorhaben.aendern.mutate({ id: vorhabenId, ...felder }),
    onMutate: async (felder) => {
      setFehler(null)
      const filter = { predicate: istVorhabenListe }
      await queryClient.cancelQueries(filter)
      const vorher = queryClient.getQueriesData<VorhabenEintrag[]>(filter)
      queryClient.setQueriesData<VorhabenEintrag[]>(filter, (alt) =>
        alt?.map((z) => (z.id === vorhabenId ? ({ ...z, ...felder } as VorhabenEintrag) : z)),
      )
      return { vorher }
    },
    onError: (e, _felder, kontext) => {
      for (const [schluessel, daten] of kontext?.vorher ?? [])
        queryClient.setQueryData(schluessel, daten)
      setFehler(
        `Konnte nicht gespeichert werden – es steht wieder der letzte gespeicherte Stand da. (${e.message})`,
      )
    },
    onSettled: neuLaden,
  })
  return { aendern: mutation.mutate, fehler }
}

/**
 * **Name, Typ, Entwicklungsart, Zieltermin, Zeitfenster** – die Eckdaten, oben im Fenster.
 *
 * Bearbeitbar für das Team **und** für Eingeladene ([E-440](../../../docs/ENTSCHEIDUNGEN.md#e-440):
 * *„alles in einem Vorhaben"*). Die Entwicklungsart gab es bis dahin nur beim Anlegen.
 */
export function Eckdaten({ v, darfAendern }: { v: VorhabenEintrag; darfAendern: boolean }) {
  const { aendern, fehler } = useAendern(v.id)

  return (
    <div className="mt-3">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-[2fr_1fr_1fr]">
        <Feld label="Name" breit>
          <input
            defaultValue={v.label}
            key={`label-${v.id}-${v.label}`}
            disabled={!darfAendern}
            className={EINGABE}
            onBlur={(e) => {
              const wert = e.target.value.trim()
              if (wert.length >= 3 && wert !== v.label) aendern({ label: wert })
            }}
          />
        </Feld>
        <Feld label="Typ" hinweis="jederzeit änderbar">
          <select
            className={EINGABE}
            value={v.typ}
            disabled={!darfAendern}
            onChange={(e) => aendern({ typ: e.target.value as 'projekt' | 'thema' })}
          >
            <option value="thema">Thema</option>
            <option value="projekt">Projekt</option>
          </select>
        </Feld>
        <Feld label="Entwicklung">
          <select
            className={EINGABE}
            value={v.entwicklungsart}
            disabled={!darfAendern}
            onChange={(e) =>
              aendern({ entwicklungsart: e.target.value as 'klassisch' | 'ki' | 'gemischt' })
            }
          >
            <option value="klassisch">Klassisch</option>
            <option value="ki">🤖 KI-Programmierung</option>
            <option value="gemischt">Gemischt</option>
          </select>
        </Feld>
        <Feld label="Zieltermin">
          <input
            type="date"
            defaultValue={v.zieltermin ? String(v.zieltermin).slice(0, 10) : ''}
            key={`ziel-${v.id}`}
            disabled={!darfAendern}
            className={EINGABE}
            onChange={(e) =>
              aendern({ zieltermin: e.target.value ? new Date(e.target.value) : null })
            }
          />
        </Feld>
        {/**
         * **Das Zeitfenster bleibt neben dem Zieltermin** – Aufgaben ohne eigenes Fenster erben
         * es ([E-108](../../../docs/ENTSCHEIDUNGEN.md)), und `zeitfenster_ab` ist der Anker, ab
         * dem die Frist läuft.
         */}
        <Feld label="Zeitfenster">
          <select
            className={EINGABE}
            value={v.zeitfenster ?? ''}
            disabled={!darfAendern}
            onChange={(e) =>
              aendern({ zeitfenster: (e.target.value || null) as Zeitfenster | null })
            }
          >
            <option value="">– nicht eingeordnet –</option>
            {ZEITFENSTER.map((z) => (
              <option key={z} value={z}>
                {ZEITFENSTER_LABELS[z]}
              </option>
            ))}
          </select>
        </Feld>
      </div>
      {fehler && <p className="mt-1.5 text-[11.5px] text-pool-sofort">{fehler}</p>}
    </div>
  )
}

/**
 * **Wer: Mit-Owner, Wunsch-Entwickler, Eingeladene.**
 *
 * Mit-Owner und Wunsch setzt das Team; Eingeladene sehen sie als Text. Die Eingeladenen stehen
 * direkt unter dem Mit-Owner, weil dort danach gefragt wurde (*„wo kriegt er denn jetzt eine
 * Mail?"*) – das Feld allein verschickt nichts.
 */
export function Wer({ v, team }: { v: VorhabenEintrag; team: boolean }) {
  const neuLaden = useNeuLaden()
  /** Nur wer in dieser Arbeitswelt ist (Z-07) – sonst sähe er die Aufgabe nie. */
  const { personen: leute } = usePersonenDieserWelt()
  /** Der Mit-Owner darf von außerhalb der Welt kommen (E-355). */
  const abnahme = usePersonenFuerAbnahme()

  /**
   * **Die gewählten Wunsch-Entwickler, solange der Server noch nicht geantwortet hat**
   * (28.08.2026). `null` heißt „es gilt, was vom Server kam" – ohne die Überlagerung sprang die
   * Auswahl bei jedem Klick zurück.
   */
  const [wunschLokal, setWunschLokal] = useState<string[] | null>(null)
  const [wunschFehler, setWunschFehler] = useState(false)

  const personen = useMutation({
    mutationFn: (felder: { mitOwnerPersonId?: string | null; wunschPersonIds?: string[] }) =>
      trpc.vorhaben.personenSetzen.mutate({ id: v.id, ...felder }),
    onSuccess: async () => {
      await neuLaden()
      /** Erst nach dem Nachladen loslassen – sonst flackert der alte Stand dazwischen. */
      setWunschLokal(null)
      setWunschFehler(false)
    },
    onError: () => {
      setWunschLokal(null)
      setWunschFehler(true)
    },
  })

  /**
   * **Zur Wahl stehen die Entwickler:innen** ([E-199](../../../docs/ENTSCHEIDUNGEN.md)). Wer schon
   * eingetragen ist, bleibt in der Liste, auch wenn er nicht mehr dazugehört – der Name kommt
   * dann aus `wunschEntwickler` (dasselbe gefilterte Array wie `wunschPersonIds`), nicht aus der
   * auf die Welt verengten Personenliste (28.08.2026: dreimal „unbekannte Person").
   */
  const entwickler = [
    ...leute.filter((p) => p.isActive && zaehltAlsEntwickler(p)),
    ...v.wunschPersonIds
      .map((id, i) => ({ id, displayName: v.wunschEntwickler[i] ?? 'unbekannte Person' }))
      .filter((p) => !leute.some((l) => l.isActive && zaehltAlsEntwickler(l) && l.id === p.id)),
  ]
  const niemandZurWahl = leute.filter((p) => p.isActive && zaehltAlsEntwickler(p)).length === 0

  return (
    <Abschnitt titel="Wer">
      {team ? (
        <>
          <Feld label="Mit-Owner" hinweis="schaut fachlich mit und nimmt ab – kein Bearbeiter">
            <select
              className={EINGABE}
              value={v.mitOwnerPersonId ?? ''}
              onChange={(e) => personen.mutate({ mitOwnerPersonId: e.target.value || null })}
            >
              <option value="">– niemand –</option>
              <PersonenOptionen inWelt={abnahme.inWelt} weitere={abnahme.weitere} />
            </select>
          </Feld>
          <VorhabenBeteiligte
            vorhabenId={v.id}
            beteiligte={v.beteiligte}
            mitOwnerPersonId={v.mitOwnerPersonId}
            mitOwnerName={v.mitOwnerName}
            darfEinladen
          />
          <WunschWahl
            alle={entwickler}
            gewaehlt={wunschLokal ?? v.wunschPersonIds}
            fehler={wunschFehler}
            umschalten={(id) => {
              const jetzt = wunschLokal ?? v.wunschPersonIds
              const neu = jetzt.includes(id) ? jetzt.filter((x) => x !== id) : [...jetzt, id]
              setWunschLokal(neu)
              setWunschFehler(false)
              personen.mutate({ wunschPersonIds: neu })
            }}
          />
          {/** Der Hinweis nennt die Stelle, an der es zu beheben ist (28.08.2026). */}
          {niemandZurWahl && (
            <p className="rounded-lg bg-[#fdf3dc] px-3 py-2 text-[11.5px] leading-relaxed text-[#8a6100]">
              In dieser Arbeitswelt ist niemand als Entwickler:in zugeordnet – deshalb steht oben
              nichts zur Wahl. Die Zuordnung macht die <strong>Benutzerverwaltung</strong> in den
              Einstellungen, Spalte „Workspaces".
            </p>
          )}
        </>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink2">
            <span>Mit-Owner: {v.mitOwnerName ?? '–'}</span>
            <span>Wunsch: {v.wunschEntwickler.length ? v.wunschEntwickler.join(', ') : '–'}</span>
          </div>
          <VorhabenBeteiligte
            vorhabenId={v.id}
            beteiligte={v.beteiligte}
            mitOwnerPersonId={v.mitOwnerPersonId}
            darfEinladen={false}
          />
        </>
      )}
    </Abschnitt>
  )
}

/**
 * **Steuerung: Nachfluss, Pool, Löschen** – nur für das Team, am Ende des Fensters.
 *
 * Das ist Mechanik der Entwicklung, nicht Inhalt des Vorhabens; deshalb unten und nicht neben
 * der Beschreibung. Der Befund „trocken" steht über den Feldern, die ihn beheben.
 */
export function Steuerung({ v, geloescht }: { v: VorhabenEintrag; geloescht: () => void }) {
  const queryClient = useQueryClient()
  const neuLaden = useNeuLaden()
  const { aendern, fehler } = useAendern(v.id)
  const { data: allePools = [] } = useQuery(poolsQuery)
  /** Ein Vorhaben zeigt auf einen Vorrat, nicht auf ein Regal ([E-191](../../../docs/ENTSCHEIDUNGEN.md)). */
  const pools = nurVorraete(allePools)

  /** Der Warnkasten gilt nur, wo der Nachfluss auch gewollt ist (E-256). */
  const trocken = v.stand.offen > 0 && v.nachflussGewuenscht && !nachflussLaeuft(v)
  /** Auch erledigte zählen – der Server sieht es genauso (`vorhaben.loeschen`). */
  const hatAufgaben = v.stand.gesamt > 0

  /**
   * **Löschen, solange keine Aufgabe dranhängt** (15.10.2026). Nicht optimistisch: das Fenster
   * bleibt offen, bis die Antwort da ist, und zeigt sie gegebenenfalls.
   */
  const [loeschFehler, setLoeschFehler] = useState<string | null>(null)
  const loeschen = useMutation({
    mutationFn: () => trpc.vorhaben.loeschen.mutate({ id: v.id }),
    onMutate: () => setLoeschFehler(null),
    onSuccess: async () => {
      geloescht()
      await Promise.all([neuLaden(), queryClient.invalidateQueries({ queryKey: ['schirm'] })])
    },
    onError: (e) => setLoeschFehler(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.'),
  })

  return (
    <Abschnitt titel="Steuerung (Team)">
      {trocken && (
        <div className="flex items-start gap-2.5 rounded-lg bg-[#fdf3dc] px-3 py-2.5 text-[12px] text-[#8a6100]">
          <strong className="tabular shrink-0 text-[13px]">
            {v.stand.imPool} / {v.nachflussLimit ?? 0}
          </strong>
          <span>
            Der Nachfluss steht. {v.stand.offen}{' '}
            {v.stand.offen === 1 ? 'offene Aufgabe erreicht' : 'offene Aufgaben erreichen'} gerade
            keinen Entwickler – Pool und Grenze hier einstellen.
          </span>
        </div>
      )}

      {/**
       * **Negativ beschriftet, positiv gespeichert** ([E-256](../../../docs/ENTSCHEIDUNGEN.md#e-256)).
       * Das Häkchen markiert die Ausnahme; die Spalte heißt `nachfluss_gewuenscht`.
       */}
      <label className="flex cursor-pointer items-start gap-2 text-[12px]">
        <input
          type="checkbox"
          checked={!v.nachflussGewuenscht}
          onChange={(e) => aendern({ nachflussGewuenscht: !e.target.checked })}
          className="mt-0.5 cursor-pointer"
        />
        <span>
          <span className="font-semibold">Nachfluss nicht gewünscht</span>
          <span className="block text-[11px] text-muted">
            Für Sammelstellen und Ablage-Themen: die Aufgaben holt sich jemand von Hand. Das
            Vorhaben wird dann nicht mehr als „trocken" gemeldet.
          </span>
        </span>
      </label>

      <div className="grid grid-cols-2 gap-2.5">
        <Feld label="Pool">
          <select
            className={EINGABE}
            value={v.poolId ?? ''}
            onChange={(e) => aendern({ poolId: e.target.value || null })}
          >
            <option value="">– keiner –</option>
            {pools.map((p) => (
              <option key={p.id} value={p.id}>
                {p.icon} {p.label}
              </option>
            ))}
          </select>
        </Feld>
        <Feld label="Höchstens im Pool">
          <input
            inputMode="numeric"
            defaultValue={v.nachflussLimit ?? ''}
            key={`limit-${v.id}`}
            placeholder="ohne"
            className={EINGABE}
            onBlur={(e) => {
              const roh = e.target.value.trim()
              const zahl = roh === '' ? null : Number(roh)
              if (zahl !== null && !Number.isFinite(zahl)) return
              if (zahl !== v.nachflussLimit) aendern({ nachflussLimit: zahl })
            }}
          />
        </Feld>
      </div>
      {/** Der Erklärtext sagt, was *jetzt* gilt – nicht, was allgemein gilt. */}
      <p className="text-[11.5px] leading-relaxed text-muted">
        {v.nachflussGewuenscht ? (
          <>
            Der Nachfluss füllt den Pool von selbst nach, sobald dort Platz ist. Ohne Pool oder ohne
            Grenze fließt nichts – die Aufgaben muss sich dann jemand von Hand aus „Alle Aufgaben"
            holen.
          </>
        ) : (
          <>
            Hier fließt nichts nach, auch mit Pool und Grenze nicht – das Häkchen oben hat Vorrang.
            Die Aufgaben holt sich jemand von Hand aus „Alle Aufgaben".
          </>
        )}
      </p>
      {fehler && <p className="text-[11.5px] text-pool-sofort">{fehler}</p>}

      <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <Button
          variant="ghost"
          size="sm"
          disabled={hatAufgaben || loeschen.isPending}
          title={
            hatAufgaben
              ? 'Löschen geht erst, wenn keine Aufgabe mehr an diesem Vorhaben hängt.'
              : undefined
          }
          onClick={() => {
            if (
              window.confirm(
                `„${v.label}" löschen? Meilensteine, Kommentare, Anhänge, Wunsch-Entwickler, Einladungen, Bezüge und Pins gehen mit. Das lässt sich nicht zurücknehmen.`,
              )
            )
              loeschen.mutate()
          }}
          className="text-pool-sofort disabled:cursor-not-allowed disabled:opacity-50"
        >
          Vorhaben löschen
        </Button>
        {hatAufgaben && (
          <span className="text-[11px] text-muted">
            Löschen geht erst, wenn keine Aufgabe mehr daran hängt – {v.stand.gesamt}{' '}
            {v.stand.gesamt === 1 ? 'Aufgabe ist' : 'Aufgaben sind'} noch zugeordnet.
          </span>
        )}
        {loeschFehler && (
          <p role="alert" className="text-[11.5px] text-pool-sofort">
            {loeschFehler}
          </p>
        )}
      </div>
    </Abschnitt>
  )
}

/**
 * **Die Vorhaben-Listen im Zwischenspeicher** – `['vorhaben', 'alle', …]` und
 * `['vorhaben', 'alle-welten']`. Nicht einfach `['vorhaben']`: darunter liegen auch Meilensteine
 * und Kommentare, und die haben andere Zeilen.
 */
function istVorhabenListe(q: { queryKey: readonly unknown[] }): boolean {
  return (
    q.queryKey[0] === 'vorhaben' && (q.queryKey[1] === 'alle' || q.queryKey[1] === 'alle-welten')
  )
}

/** Eine Klasse für alle Eingaben – neun Felder mit neun Klassenlisten laufen auseinander. */
const EINGABE =
  'mt-0.5 w-full rounded-md border border-line bg-page px-2 py-1.5 text-[12.5px] focus:border-accent focus:bg-surface disabled:opacity-70'

export function Abschnitt({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div className="mt-4 flex flex-col gap-2.5">
      <h4 className="text-xs font-bold">{titel}</h4>
      {children}
    </div>
  )
}

function Feld({
  label,
  hinweis,
  breit = false,
  children,
}: {
  label: string
  hinweis?: string
  /** Über beide Spalten, wo das Raster zweispaltig ist (Telefon). */
  breit?: boolean
  children: React.ReactNode
}) {
  return (
    <label className={`block min-w-0 text-[11.5px] ${breit ? 'col-span-2 sm:col-span-1' : ''}`}>
      <span className="text-muted">
        {label}
        {hinweis && <span className="ml-1 text-[10.5px]">— {hinweis}</span>}
      </span>
      {children}
    </label>
  )
}

/**
 * **Wunsch-Entwickler zum Antippen** (28.08.2026). Chips statt `<select multiple>`: das
 * kontrollierte Mehrfachfeld sprang bei jedem Klick auf den Serverstand zurück, war am Telefon
 * nicht bedienbar und versteckte den sechsten Namen. `aria-pressed`: es sind Schalter.
 */
function WunschWahl({
  alle,
  gewaehlt,
  fehler,
  umschalten,
}: {
  alle: { id: string; displayName: string }[]
  gewaehlt: string[]
  fehler: boolean
  umschalten: (id: string) => void
}) {
  return (
    <div className="text-[11.5px]">
      <span className="text-muted">
        Wunsch-Entwickler
        <span className="ml-1 text-[10.5px]">— mehrere möglich, tippen schaltet um</span>
      </span>

      {alle.length === 0 ? (
        <p className="mt-1 text-muted">Niemand zur Wahl.</p>
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {alle.map((p) => {
            const an = gewaehlt.includes(p.id)
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={an}
                onClick={() => umschalten(p.id)}
                className={`cursor-pointer rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
                  an
                    ? 'border-transparent bg-vorhaben-bg font-semibold text-pool-projekte'
                    : 'border-line bg-page text-ink2 hover:border-line2 hover:text-ink'
                }`}
              >
                {p.displayName}
              </button>
            )
          })}
        </div>
      )}

      {fehler && (
        <p className="mt-1.5 text-[11.5px] text-pool-sofort">
          Konnte nicht gespeichert werden – die Auswahl steht wieder auf dem letzten gespeicherten
          Stand.
        </p>
      )}
    </div>
  )
}
